import { Alert, Button, List, message, Modal, Popconfirm, Space, Spin, Tag, Typography } from "antd";
import { useMemo, useState } from "react";
import dayjs from "dayjs";

import { ApiService } from "api/api.service";
import { PaymentJobDto, PaymentJobStatus } from "dtos/payment.job.dto";
import { TbAlertCircle, TbExternalLink } from "react-icons/tb";
import { getJobTitleLabel } from "pages/PaymentJobs/helpers";

const { Text } = Typography;

type Props = {
  application_id: string;
  appkey: string;
  open: boolean;
  onClose: () => void;
  refetch: () => void;
};

export const ControlPaymentAutomation = ({ application_id, open, onClose, refetch, appkey }: Props) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const jobsQuery = ApiService.paymentJob.useGetJobsForPreview({
    application_id,
  });

  const stopAutomation = ApiService.paymentJob.useCancelAllApplicationPaymentJobs();

  const jobs: PaymentJobDto[] = jobsQuery.data?.result ?? [];

  const hasUnsafeJob = useMemo(() => {
    const now = dayjs();

    return jobs.some((job) => {
      if (job.status === PaymentJobStatus.RUNNING) {
        return true;
      }

      if (job.status !== PaymentJobStatus.SCHEDULED) {
        return false;
      }

      const scheduledAt = dayjs(job.scheduled_at);

      return scheduledAt.isAfter(now) && scheduledAt.diff(now, "minute", true) <= 5;
    });
  }, [jobs]);

  const handleStopAutomation = () => {
    stopAutomation.mutate(
      {
        application_id,
      },
      {
        onSuccess: () => {
          refetch();
          onClose();

          message.success("All automation jobs have been cancelled for this application.");
        },
      },
    );
  };

  const stopButton = (
    <Button danger loading={stopAutomation.isPending} disabled={hasUnsafeJob} onClick={handleStopAutomation}>
      Stop Automation
    </Button>
  );

  return (
    <Modal open={open} onCancel={onClose} footer={null} title="Control Payment Automation" destroyOnClose>
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {jobsQuery.isLoading ? (
          <Spin />
        ) : jobs.length === 0 ? (
          <Alert type="success" message="No upcoming jobs. Automation can be safely stopped." />
        ) : (
          <>
            <Alert
              type="error"
              icon={<TbAlertCircle />}
              showIcon
              message="Upcoming payment jobs detected."
              description="Stopping automation will fully stop and remove all future payment automation flows. You will have to restart manually."
            />

            <List
              bordered
              dataSource={jobs}
              renderItem={(job) => (
                <List.Item
                  actions={[
                    <Button
                      type="link"
                      key={`${job._id}`}
                      icon={<TbExternalLink size={18} />}
                      onClick={() =>
                        window.open(
                          `/ledger/payment-monitor?ledger_id=${job.ledger_id}&application_id=${application_id}&appkey=${appkey}`,
                          "_blank",
                        )
                      }
                    ></Button>,
                  ]}
                >
                  <Space direction="vertical" size={0} style={{ width: "100%" }}>
                    <Space wrap>
                      <Text strong>{getJobTitleLabel(job.job_name)}</Text>

                      <Tag color="geekblue">Week {job.week}</Tag>

                      {/* <Tag color="#64748b">{job.status.toUpperCase()}</Tag> */}
                    </Space>

                    <Text type="secondary">Scheduled: {dayjs(job.scheduled_at).format("MMM D, YYYY h:mm A")}</Text>
                  </Space>
                </List.Item>
              )}
            />
          </>
        )}

        {hasUnsafeJob && (
          <Alert
            type="error"
            message="Automation cannot currently be stopped."
            description="A payment job is either running or scheduled within the next 5 minutes."
          />
        )}

        <Space
          style={{
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Button onClick={onClose}>Close</Button>

          {jobs.length === 0 ? (
            stopButton
          ) : (
            <Popconfirm
              open={confirmOpen}
              onOpenChange={setConfirmOpen}
              title="Stop payment automation?"
              description="This will fully stop all future automation flows. Do you want to proceed?"
              okText="Stop Automation"
              cancelText="Cancel"
              onConfirm={handleStopAutomation}
              disabled={hasUnsafeJob}
            >
              <Button danger loading={stopAutomation.isPending} disabled={hasUnsafeJob}>
                Stop Automation
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Space>
    </Modal>
  );
};
